/**
 * Global error handler for the ClubEzy Administration Portal.
 *
 * Captures unhandled exceptions from:
 * - Component rendering failures
 * - Unhandled promise rejections
 * - HTTP errors
 * - RxJS observable errors
 *
 * Errors are forwarded to Sentry in every environment that enables it
 * (`environment.sentry.enabled` — dev, staging and production; not local),
 * and logged to the console with full stack traces outside production.
 */
import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { buildInfo } from '@/app/core/app-info/build-info.generated';
import { environment } from '@/environments/environment';

// Initialise Sentry once at module load time.
//
// `enabled` is deliberately NOT tied to `environment.production`: staging and
// dev both run with `production: false`, so gating on it left every
// environment except production reporting nothing at all.
if (environment.sentry.enabled && environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.envName,
    // Ties each issue to the deployed build. Without it Sentry lumps every
    // deploy into one release and "which build regressed?" is unanswerable.
    release: buildInfo.version,
  });
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    // Unwrap Angular/Zone wrapper to get the original error
    const originalError = this.extractOriginalError(error);
    const message =
      originalError instanceof Error
        ? originalError.message
        : String(originalError);
    const stack =
      originalError instanceof Error ? originalError.stack : undefined;

    Sentry.captureException(originalError);

    if (environment.production) {
      // ── Production: structured logging ─────────────────────────────
      console.error('[ClubEzy Error]', {
        message,
        stack,
        timestamp: new Date().toISOString(),
        url: globalThis.location?.href,
        userAgent: globalThis.navigator?.userAgent,
      });
    } else {
      // ── Development: loud console output ───────────────────────────
      console.error('🔴 [ClubEzy Dev Error]', originalError);
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────

  /**
   * Unwrap Angular's error wrapping to get the original error object.
   */
  private extractOriginalError(error: unknown): unknown {
    if (error && typeof error === 'object') {
      if ('rejection' in error) {
        return (error as any).rejection;
      }
      if ('ngOriginalError' in error) {
        return (error as any).ngOriginalError;
      }
    }
    return error;
  }
}
